import React, { useState, useEffect, useRef } from 'react';
import { Search, X, BookOpen, Copy, Check, Filter, Sparkles, FileText, Zap, BookMarked, Download, Shield, ChevronDown, Layers, Scale, Maximize2, Minimize2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

interface LegalTerm {
  id: string;
  term: string;
  pronunciation?: string;
  category: string;
  source: string;
  definition: string;
  genericMeaning?: string;
  lawMeaning?: string;
  asLawTerm?: string;
  citedSource?: string;
  latinMaxim?: string;
  courtUsage?: string;
  relatedTerms?: string[];
}

interface StatuteItem {
  id: string;
  title: string;
  year: string;
  category: string;
  jurisdiction: string;
  legislation_type: string;
  issuing_authority?: string;
  short_description: string;
  semantic_bullets: string[];
  detailed_theme: string;
  brand: string;
  logo: string;
  header: string;
  footer: string;
  source_url?: string;
  pdf_download_available?: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'dictionary' | 'statutes';
  initialCategory?: string;
}

export const BlacksLawDictionaryModal: React.FC<Props> = ({ 
  isOpen, 
  onClose,
  initialTab = 'dictionary',
  initialCategory
}) => {
  const [activeMainTab, setActiveMainTab] = useState<'dictionary' | 'statutes'>(initialTab);
  
  // DICTIONARY STATES
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [globalViewMode, setGlobalViewMode] = useState<'short' | 'detailed'>('short');
  const [expandedCardIds, setExpandedCardIds] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [terms, setTerms] = useState<LegalTerm[]>([]);
  const [dictLoading, setDictLoading] = useState(false);

  // STATUTES STATES
  const [statuteSearch, setStatuteSearch] = useState('');
  const [statuteCategory, setStatuteCategory] = useState(initialCategory || 'All');
  const [statuteJurisdiction, setStatuteJurisdiction] = useState('All');
  const [statuteYear, setStatuteYear] = useState('All');
  const [statutesList, setStatutesList] = useState<StatuteItem[]>([]);
  const [statutesLoading, setStatutesLoading] = useState(false);
  const [statuteViewMode, setStatuteViewMode] = useState<'brief' | 'detailed'>('brief');
  const [expandedStatuteIds, setExpandedStatuteIds] = useState<Record<string, boolean>>({});

  const dictCategories = ['All', 'General Principles', 'Civil & Procedure', 'Constitutional Law', 'Criminal Law', 'Jurisprudence', 'Evidence & Procedure', 'Contract & Commercial', 'Property Law', 'Equity & Trust'];
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const statuteCategoriesList = [
    'All',
    'Constitutional Law',
    'Criminal & Penal Code',
    'Civil Procedure & Property',
    'Fiscal & Revenue Laws / SROs',
    'Corporate & Commercial Law',
    'Labor, Employment & Service',
    'Environmental, Land & Local Govt',
    'General Statutes & Notifications'
  ];

  const statuteJurisdictionsList = [
    'All',
    'Federal / Supreme Court',
    'Punjab',
    'Sindh',
    'Khyber Pakhtunkhwa',
    'Balochistan',
    'Federal / Provincial SROs'
  ];

  useEffect(() => {
    if (initialTab) setActiveMainTab(initialTab);
    if (initialCategory) setStatuteCategory(initialCategory);
  }, [initialTab, initialCategory, isOpen]);

  // Fetch Dictionary Terms
  useEffect(() => {
    if (!isOpen || activeMainTab !== 'dictionary') return;

    const fetchTerms = async () => {
      setDictLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('q', searchTerm);
        if (selectedLetter) params.append('letter', selectedLetter);
        if (selectedCategory && selectedCategory !== 'All') params.append('category', selectedCategory);

        const res = await fetch(`${api.defaults.baseURL}/dictionary/terms?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setTerms(data.terms || []);
        } else {
          setTerms([]);
        }
      } catch (err) {
        setTerms([]);
      } finally {
        setDictLoading(false);
      }
    };

    fetchTerms();
  }, [isOpen, activeMainTab, searchTerm, selectedLetter, selectedCategory]);

  // Fetch Statutes with robust API + local fallback
  useEffect(() => {
    if (!isOpen || activeMainTab !== 'statutes') return;

    const fetchStatutes = async () => {
      setStatutesLoading(true);

      const filterLocal = async () => {
        try {
          const jsonRes = await fetch('/elum_statutes_matrix_full.json');
          if (jsonRes.ok) {
            const raw: any[] = await jsonRes.json();
            return raw.filter((item: any) => {
              const title = (item.title || item.name || '').toLowerCase();
              const desc = (item.short_description || item.detailed_theme || '').toLowerCase();
              const cat = (item.category || '').toLowerCase();
              const juris = (item.jurisdiction || '').toLowerCase();
              const yr = (item.year || '').toString();

              if (statuteSearch.trim()) {
                const q = statuteSearch.toLowerCase().trim();
                if (!title.includes(q) && !desc.includes(q) && !cat.includes(q) && !juris.includes(q) && !yr.includes(q)) {
                  return false;
                }
              }
              if (statuteCategory && statuteCategory !== 'All') {
                if (!cat.includes(statuteCategory.toLowerCase())) return false;
              }
              if (statuteJurisdiction && statuteJurisdiction !== 'All') {
                if (!juris.includes(statuteJurisdiction.toLowerCase())) return false;
              }
              if (statuteYear && statuteYear !== 'All') {
                if (yr !== statuteYear.toString()) return false;
              }
              return true;
            }).slice(0, 60);
          }
        } catch (e) {}
        return [];
      };

      try {
        const params = new URLSearchParams();
        if (statuteSearch) params.append('q', statuteSearch);
        if (statuteCategory && statuteCategory !== 'All') params.append('category', statuteCategory);
        if (statuteJurisdiction && statuteJurisdiction !== 'All') params.append('jurisdiction', statuteJurisdiction);
        if (statuteYear && statuteYear !== 'All') params.append('year', statuteYear);
        params.append('limit', '60');

        const res = await fetch(`${api.defaults.baseURL}/statutes?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.statutes) && data.statutes.length > 0) {
            setStatutesList(data.statutes);
          } else {
            setStatutesList(await filterLocal());
          }
        } else {
          setStatutesList(await filterLocal());
        }
      } catch (err) {
        setStatutesList(await filterLocal());
      } finally {
        setStatutesLoading(false);
      }
    };

    fetchStatutes();
  }, [isOpen, activeMainTab, statuteSearch, statuteCategory, statuteJurisdiction, statuteYear]);

  const toggleCardView = (id: string) => {
    setExpandedCardIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleStatuteView = (id: string) => {
    setExpandedStatuteIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopyCitation = (term: LegalTerm, isDetailed: boolean) => {
    let textToCopy = `"${term.term}" (Law Dictionary)\n`;
    if (isDetailed) {
      textToCopy += `========================================\n` +
        `DETAILED LEGAL ANALYSIS:\n` +
        `• Category: ${term.category}\n` +
        `• Generic Meaning: ${term.genericMeaning || term.definition}\n` +
        `• Law Meaning: ${term.lawMeaning || term.definition}\n` +
        `• As Law Term (Functional Application): ${term.asLawTerm || term.courtUsage || 'Statutory Application'}\n` +
        `• Full Judicial Definition: ${term.definition}\n` +
        (term.latinMaxim ? `• Latin Maxim: "${term.latinMaxim}"\n` : '') +
        `• Cited Source: ${term.citedSource || term.source || 'Law Dictionary Authority'}\n` +
        `========================================`;
    } else {
      textToCopy += `• Generic Meaning: ${term.genericMeaning || term.definition}\n` +
        `• Law Meaning: ${term.lawMeaning || term.definition}\n` +
        `• As Law Term: ${term.asLawTerm || term.courtUsage || 'Statutory Application'}\n` +
        `• Cited Source: ${term.citedSource || term.source || 'Law Dictionary Authority'}`;
    }
    navigator.clipboard.writeText(textToCopy);
    setCopiedId(term.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadBrandedPDF = (statute: StatuteItem) => {
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${statute.title} — Elum Law Associates Document</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&family=Playfair+Display:ital,wght@0,700;0,900;1,700&display=swap');
    body {
      font-family: 'Inter', sans-serif;
      margin: 0;
      padding: 0;
      background-color: #ffffff;
      color: #0f172a;
      -webkit-print-color-adjust: exact;
    }
    .header-bar {
      background: linear-gradient(135deg, #090d16 0%, #1e293b 100%);
      color: #ffffff;
      padding: 24px 32px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 3px solid #ecc94b;
    }
    .brand-section {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .brand-logo {
      height: 48px;
      width: 48px;
      border-radius: 50%;
      border: 2px solid #ecc94b;
    }
    .brand-title {
      font-family: 'Playfair Display', serif;
      font-size: 22px;
      font-weight: 900;
      color: #ecc94b;
      letter-spacing: 0.5px;
      margin: 0;
    }
    .brand-sub {
      font-size: 11px;
      color: #94a3b8;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-top: 2px;
    }
    .doc-container {
      padding: 32px;
      max-width: 800px;
      margin: 0 auto;
    }
    .statute-title {
      font-family: 'Playfair Display', serif;
      font-size: 24px;
      font-weight: 900;
      color: #0f172a;
      margin: 0 0 12px 0;
      line-height: 1.3;
    }
    .meta-badges {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 24px;
    }
    .badge {
      font-size: 11px;
      font-weight: 800;
      padding: 4px 10px;
      border-radius: 4px;
      background-color: #f1f5f9;
      color: #334155;
      border: 1px solid #cbd5e1;
      text-transform: uppercase;
    }
    .badge-gold {
      background-color: #ecc94b;
      color: #090d16;
      border-color: #d69e2e;
    }
    .section-box {
      margin-bottom: 24px;
      background: #f8fafc;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      padding: 20px;
    }
    .section-title {
      font-size: 13px;
      font-weight: 800;
      color: #0f172a;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 6px;
      border-bottom: 1px solid #e2e8f0;
      padding-bottom: 8px;
    }
    .bullet-list {
      margin: 0;
      padding-left: 20px;
    }
    .bullet-item {
      font-size: 13px;
      color: #334155;
      line-height: 1.6;
      margin-bottom: 8px;
    }
    .theme-box {
      font-size: 13px;
      line-height: 1.8;
      color: #1e293b;
      white-space: pre-wrap;
    }
    .footer-banner {
      border-top: 1px solid #e2e8f0;
      padding: 20px 32px;
      text-align: center;
      font-size: 11px;
      color: #64748b;
    }
    @media print {
      body {
        padding: 0;
      }
      .doc-container {
        padding: 16px;
      }
    }
  </style>
</head>
<body>
  <div class="header-bar">
    <div class="brand-section">
      <img src="https://www.elumlaw.com/logo_ELum_Firm4.png" class="brand-logo" alt="Elum Logo" />
      <div>
        <h1 class="brand-title">ELUM LAW ASSOCIATES</h1>
        <div class="brand-sub">Pakistan's AI-Powered Judicial Node • Central Repository</div>
      </div>
    </div>
    <div style="text-align: right; font-size: 11px; color: #ecc94b; font-weight: bold;">
      STATUTORY RECORD<br/>AUTHENTICATED REPOSITORY
    </div>
  </div>

  <div class="doc-container">
    <h1 class="statute-title">${statute.title}</h1>
    <div class="meta-badges">
      <span class="badge badge-gold">YEAR: ${statute.year}</span>
      <span class="badge">CATEGORY: ${statute.category}</span>
      <span class="badge">JURISDICTION: ${statute.jurisdiction}</span>
      <span class="badge">LEGISLATION TYPE: ${statute.legislation_type}</span>
    </div>

    <div class="section-box">
      <div class="section-title">⚖️ Executive Brief & 5-Tier Semantic Provisions</div>
      <ul class="bullet-list">
        ${statute.semantic_bullets ? statute.semantic_bullets.map(b => `<li class="bullet-item">${b}</li>`).join('') : ''}
      </ul>
    </div>

    <div class="section-box">
      <div class="section-title">📜 Detailed Original Theme & Statutory Text</div>
      <div class="theme-box">${statute.detailed_theme}</div>
    </div>
  </div>

  <div class="footer-banner">
    <div><strong>Elum Law Associates — Pakistan Supreme Court & High Court Chambers</strong></div>
    <div style="margin-top: 4px;">© Elum Law Associates — All Rights Reserved. Errors & Omissions Excepted (E&OE).</div>
  </div>

  <script>
    window.onload = function() {
      window.print();
    }
  </script>
</body>
</html>
    `;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      alert("Please allow popups to download/print statute PDF.");
    }
  };

  const { colors, themeMode, setThemeMode } = useTheme();

  // WINDOW WITHIN WINDOW CONTROLS & DYNAMIC RESIZING (Horizontal & Vertical Drag)
  const [windowState, setWindowState] = useState<'normal' | 'fullscreen' | 'minimized'>('normal');
  const [windowSize, setWindowSize] = useState<{ width: number; height: number }>({ width: 1100, height: 720 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  // LISTEN FOR GLOBAL 'openLawDictionary' EVENT FROM NAV BAR BADGES TO RESTORE FROM MINIMIZED
  useEffect(() => {
    const handleRestore = () => {
      setWindowState('normal');
    };
    window.addEventListener('openLawDictionary', handleRestore);
    return () => window.removeEventListener('openLawDictionary', handleRestore);
  }, []);

  // MOUSE DRAG EVENT HANDLERS FOR DYNAMIC WINDOW RESIZING
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      const newW = Math.max(450, Math.min(window.innerWidth - 30, resizeRef.current.startW + dx));
      const newH = Math.max(400, Math.min(window.innerHeight - 30, resizeRef.current.startH + dy));
      setWindowSize({ width: newW, height: newH });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleStartResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: windowSize.width,
      startH: windowSize.height
    };
  };

  if (!isOpen) return null;

  // IF MINIMIZED -> DOCK AS FLOATING BADGE AT BOTTOM-RIGHT CORNER
  if (windowState === 'minimized') {
    return (
      <div 
        onClick={() => setWindowState('normal')}
        title="Click to restore Law Dictionary"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '510px',
          zIndex: 99999,
          backgroundColor: '#15803d',
          color: 'white',
          padding: '0.85rem 1.35rem',
          borderRadius: '50px',
          cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(21, 128, 61, 0.5), 0 4px 10px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontWeight: '800',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <BookOpen size={20} color="#ecc94b" />
        <span>📖 Law Dictionary</span>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: windowState === 'fullscreen' ? 0 : 'auto',
      left: windowState === 'fullscreen' ? 0 : 'auto',
      right: windowState === 'fullscreen' ? 0 : '24px',
      bottom: windowState === 'fullscreen' ? 0 : '24px',
      width: windowState === 'fullscreen' ? '100vw' : `${windowSize.width}px`,
      height: windowState === 'fullscreen' ? '100vh' : `${windowSize.height}px`,
      maxWidth: windowState === 'fullscreen' ? '100vw' : '96vw',
      maxHeight: windowState === 'fullscreen' ? '100vh' : '94vh',
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(10px)',
      zIndex: 2500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: windowState === 'fullscreen' ? 0 : '0.5rem',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      <div className="law-modal-container" style={{
        backgroundColor: colors.cardBg,
        color: colors.textColor,
        border: `2px solid ${colors.goldPrimary}`,
        borderRadius: windowState === 'fullscreen' ? '0' : '16px',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 30px rgba(236,201,75,0.25)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        position: 'relative'
      }}>
        
        {/* MODAL HEADER WITH LOGO, MAIN TABS & WINDOW CONTROLS (_ [ ] X) */}
        <div className="law-modal-header" style={{
          padding: '0.85rem 1.25rem',
          backgroundColor: colors.headerBg,
          borderBottom: `2px solid ${colors.goldPrimary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.85rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
            <img 
              src="/logo_ELum_Firm4.png" 
              alt="Logo" 
              style={{ height: '36px', width: '36px', borderRadius: '50%', border: '1.5px solid #ecc94b', objectFit: 'contain' }} 
            />
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '900', color: '#ffffff', margin: 0, letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                ELUM LAW ASSOCIATES
              </h2>
              <span style={{ fontSize: '0.72rem', color: '#ecc94b', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: '800', display: 'block' }}>
                Pakistan's AI-Powered Judicial Repository & Statutes Vault
              </span>
            </div>

            {/* MAIN TAB SWITCHER & CROSS-LINKED AI BADGES */}
            <div style={{ display: 'flex', gap: '0.4rem', backgroundColor: '#070b12', padding: '0.25rem', borderRadius: '8px', border: '1px solid #1e293b', marginLeft: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setActiveMainTab('dictionary')}
                style={{
                  padding: '0.4rem 0.85rem',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  backgroundColor: activeMainTab === 'dictionary' ? '#ecc94b' : 'transparent',
                  color: activeMainTab === 'dictionary' ? '#090d16' : '#cbd5e1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                <BookOpen size={14} /> Legal Dictionary (8,660 Terms)
              </button>
              <button
                onClick={() => setActiveMainTab('statutes')}
                style={{
                  padding: '0.4rem 0.85rem',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  backgroundColor: activeMainTab === 'statutes' ? '#ecc94b' : 'transparent',
                  color: activeMainTab === 'statutes' ? '#090d16' : '#cbd5e1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                <Scale size={14} /> Acts, Laws & SROs (9,343 Statutes)
              </button>

              <a href="/ai-assistant" style={{ textDecoration: 'none' }}>
                <span style={{ backgroundColor: '#0284c7', color: 'white', fontSize: '0.75rem', fontWeight: '800', padding: '0.4rem 0.75rem', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                  🤖 AI Assistant
                </span>
              </a>

              <a href="/law-directory" style={{ textDecoration: 'none' }}>
                <span style={{ backgroundColor: '#1e293b', color: '#38bdf8', fontSize: '0.75rem', fontWeight: '800', padding: '0.4rem 0.75rem', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #0284c7', cursor: 'pointer' }}>
                  ⚖️ Case Law Search
                </span>
              </a>
            </div>
          </div>

          {/* WINDOW ACTION BUTTONS (_ [ ] X) AT EXTREME TOP RIGHT */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            <button 
              onClick={() => setWindowState('minimized')} 
              title="Minimize to Bottom Dock" 
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
            >
              _
            </button>
            <button 
              onClick={() => setWindowState(windowState === 'fullscreen' ? 'normal' : 'fullscreen')} 
              title={windowState === 'fullscreen' ? "Restore Window Size" : "Maximize Fullscreen"} 
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {windowState === 'fullscreen' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            <button 
              onClick={onClose} 
              title="Close Dictionary Modal" 
              style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* TAB 1: LEGAL DICTIONARY CONTENT */}
        {activeMainTab === 'dictionary' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            
            {/* SEARCH & FILTERS BAR */}
            <div style={{ padding: '1rem 1.5rem', backgroundColor: '#0b1120', borderBottom: '1px solid #1e293b' }}>
              <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.85rem' }}>
                <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ecc94b' }} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search 8,660 legal terms (e.g. Habeas corpus, Bail, Res Judicata)..."
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem 0.65rem 2.4rem',
                      backgroundColor: '#070b12',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '0.88rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.4rem', backgroundColor: '#070b12', padding: '0.2rem', borderRadius: '8px', border: '1px solid #334155' }}>
                  <button
                    onClick={() => setGlobalViewMode('short')}
                    style={{
                      padding: '0.45rem 0.75rem',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      backgroundColor: globalViewMode === 'short' ? '#38bdf8' : 'transparent',
                      color: globalViewMode === 'short' ? '#090d16' : '#94a3b8'
                    }}
                  >
                    📄 4-Bullet Brief
                  </button>
                  <button
                    onClick={() => setGlobalViewMode('detailed')}
                    style={{
                      padding: '0.45rem 0.75rem',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      backgroundColor: globalViewMode === 'detailed' ? '#ecc94b' : 'transparent',
                      color: globalViewMode === 'detailed' ? '#090d16' : '#94a3b8'
                    }}
                  >
                    📜 Full Detailed Analysis
                  </button>
                </div>
              </div>

              {/* CATEGORIES */}
              <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', paddingBottom: '0.4rem' }}>
                {dictCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      padding: '0.3rem 0.65rem',
                      borderRadius: '20px',
                      border: '1px solid',
                      borderColor: selectedCategory === cat ? '#ecc94b' : '#334155',
                      backgroundColor: selectedCategory === cat ? 'rgba(236,201,75,0.15)' : 'transparent',
                      color: selectedCategory === cat ? '#ecc94b' : '#94a3b8',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* RESULTS LIST */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
              {dictLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                  Searching 8,660 master dictionary terms...
                </div>
              ) : terms.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                  No legal terms found matching your query.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                  {terms.map(t => {
                    const isExpanded = expandedCardIds[t.id] ?? (globalViewMode === 'detailed');
                    return (
                      <div key={t.id} style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#ffffff', margin: 0 }}>{t.term}</h3>
                            <span style={{ fontSize: '0.75rem', color: '#ecc94b', fontWeight: '700' }}>{t.category}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button
                              onClick={() => toggleCardView(t.id)}
                              style={{ backgroundColor: '#1e293b', color: '#38bdf8', border: '1px solid #334155', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              {isExpanded ? 'Show 4-Bullet Brief' : 'Show Full Detailed Analysis'}
                            </button>
                            <button
                              onClick={() => handleCopyCitation(t, isExpanded)}
                              style={{ backgroundColor: 'rgba(236,201,75,0.15)', color: '#ecc94b', border: '1px solid rgba(236,201,75,0.4)', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              {copiedId === t.id ? 'Copied!' : 'Copy Citation'}
                            </button>
                          </div>
                        </div>

                        {/* 4-BULLET STRUCTURED MEANING */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', backgroundColor: '#070b12', padding: '0.85rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                          <div style={{ fontSize: '0.85rem', color: '#e2e8f0' }}><strong style={{ color: '#ecc94b' }}>🔹 Generic Meaning:</strong> {t.genericMeaning || t.definition}</div>
                          <div style={{ fontSize: '0.85rem', color: '#e2e8f0' }}><strong style={{ color: '#38bdf8' }}>⚖️ Law Meaning:</strong> {t.lawMeaning || t.definition}</div>
                          <div style={{ fontSize: '0.85rem', color: '#e2e8f0' }}><strong style={{ color: '#4ade80' }}>📜 As Law Term (Functional Application):</strong> {t.asLawTerm || t.courtUsage || 'Statutory Application'}</div>
                          <div style={{ fontSize: '0.85rem', color: '#e2e8f0' }}><strong style={{ color: '#c084fc' }}>📚 Cited Source:</strong> {t.citedSource || t.source || 'Law Dictionary Authority'}</div>
                        </div>

                        {/* FULL DETAILED MODE */}
                        {isExpanded && (
                          <div style={{ marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px solid #1e293b', fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                            <div style={{ fontWeight: 'bold', color: 'white', marginBottom: '0.35rem' }}>Full Judicial Definition & Context:</div>
                            <p style={{ margin: 0, backgroundColor: '#090d16', padding: '0.75rem', borderRadius: '6px', border: '1px solid #1e293b' }}>{t.definition}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: ACTS, LAWS & SROs REPOSITORY */}
        {activeMainTab === 'statutes' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            
            {/* SEARCH & FILTERS BAR */}
            <div style={{ padding: '1rem 1.5rem', backgroundColor: '#0b1120', borderBottom: '1px solid #1e293b' }}>
              <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.85rem' }}>
                <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#ecc94b' }} />
                  <input
                    type="text"
                    value={statuteSearch}
                    onChange={(e) => setStatuteSearch(e.target.value)}
                    placeholder="Search 9,343 Acts, Statutes, Laws & SROs by keyword, title or year..."
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem 0.65rem 2.4rem',
                      backgroundColor: '#070b12',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '0.88rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.4rem', backgroundColor: '#070b12', padding: '0.2rem', borderRadius: '8px', border: '1px solid #334155' }}>
                  <button
                    onClick={() => setStatuteViewMode('brief')}
                    style={{
                      padding: '0.45rem 0.75rem',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      backgroundColor: statuteViewMode === 'brief' ? '#38bdf8' : 'transparent',
                      color: statuteViewMode === 'brief' ? '#090d16' : '#94a3b8'
                    }}
                  >
                    📄 5-Bullet Brief
                  </button>
                  <button
                    onClick={() => setStatuteViewMode('detailed')}
                    style={{
                      padding: '0.45rem 0.75rem',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      backgroundColor: statuteViewMode === 'detailed' ? '#ecc94b' : 'transparent',
                      color: statuteViewMode === 'detailed' ? '#090d16' : '#94a3b8'
                    }}
                  >
                    📜 Full Detailed Theme
                  </button>
                </div>
              </div>

              {/* JURISDICTION & CATEGORY FILTER CHIPS */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.7rem', color: '#ecc94b', fontWeight: 'bold', textTransform: 'uppercase', marginRight: '4px' }}>Jurisdiction:</span>
                  {statuteJurisdictionsList.map(j => (
                    <button
                      key={j}
                      onClick={() => setStatuteJurisdiction(j)}
                      style={{
                        padding: '0.25rem 0.55rem',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: statuteJurisdiction === j ? '#38bdf8' : '#334155',
                        backgroundColor: statuteJurisdiction === j ? 'rgba(56,189,248,0.15)' : 'transparent',
                        color: statuteJurisdiction === j ? '#38bdf8' : '#94a3b8',
                        fontSize: '0.72rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                      }}
                    >
                      {j}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.7rem', color: '#ecc94b', fontWeight: 'bold', textTransform: 'uppercase', marginRight: '4px' }}>Category:</span>
                  {statuteCategoriesList.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setStatuteCategory(cat)}
                      style={{
                        padding: '0.25rem 0.55rem',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: statuteCategory === cat ? '#ecc94b' : '#334155',
                        backgroundColor: statuteCategory === cat ? 'rgba(236,201,75,0.15)' : 'transparent',
                        color: statuteCategory === cat ? '#ecc94b' : '#94a3b8',
                        fontSize: '0.72rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* STATUTES RESULTS LIST */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
              {statutesLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                  Searching 9,343 Master Acts, Laws, and SRO Notifications...
                </div>
              ) : statutesList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                  No statutes found matching your criteria.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.25rem' }}>
                  {statutesList.map(st => {
                    const isExpanded = expandedStatuteIds[st.id] ?? (statuteViewMode === 'detailed');
                    return (
                      <div key={st.id} style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                        
                        {/* BRAND HEADER BAR */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(236,201,75,0.2)', paddingBottom: '0.65rem', marginBottom: '0.85rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <img src="/logo_ELum_Firm4.png" alt="Logo" style={{ height: '24px', width: '24px', borderRadius: '50%', border: '1px solid #ecc94b' }} />
                            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'white' }}>ELUM LAW ASSOCIATES</span>
                            <span style={{ fontSize: '0.68rem', color: '#ecc94b' }}>• Pakistan Statutory Vault</span>
                          </div>
                          <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>© All Rights Reserved (E&OE)</span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#ffffff', margin: '0 0 0.4rem 0' }}>{st.title}</h3>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <span style={{ backgroundColor: '#ecc94b', color: '#090d16', fontSize: '0.72rem', fontWeight: '800', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>YEAR: {st.year}</span>
                              <span style={{ backgroundColor: 'rgba(56,189,248,0.15)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.3)', fontSize: '0.72rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>{st.category}</span>
                              <span style={{ backgroundColor: '#1e293b', color: '#cbd5e1', fontSize: '0.72rem', fontWeight: '600', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>{st.jurisdiction}</span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button
                              onClick={() => toggleStatuteView(st.id)}
                              style={{ backgroundColor: '#1e293b', color: '#38bdf8', border: '1px solid #334155', padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              {isExpanded ? 'Show 5-Bullet Brief' : 'Show Detailed Theme'}
                            </button>

                            <button
                              onClick={() => handleDownloadBrandedPDF(st)}
                              style={{ backgroundColor: '#ecc94b', color: '#090d16', border: 'none', padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                              <Download size={14} /> Download Branded PDF
                            </button>
                          </div>
                        </div>

                        {/* 4-5 SEMANTIC BULLETS BRIEF */}
                        <div style={{ backgroundColor: '#070b12', padding: '0.85rem', borderRadius: '8px', border: '1px solid #1e293b', marginBottom: '0.5rem' }}>
                          <div style={{ fontSize: '0.75rem', color: '#ecc94b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                            ⚖️ Executive Brief & 5-Tier Semantic Provisions:
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#e2e8f0', fontSize: '0.83rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                            {st.semantic_bullets && st.semantic_bullets.map((bullet, idx) => (
                              <li key={idx}>{bullet}</li>
                            ))}
                          </ul>
                        </div>

                        {/* DETAILED THEME VIEW */}
                        {isExpanded && (
                          <div style={{ marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px solid #1e293b' }}>
                            <div style={{ fontSize: '0.8rem', color: '#38bdf8', fontWeight: 'bold', marginBottom: '0.4rem' }}>📜 Detailed Original Theme & Statutory Text:</div>
                            <pre style={{ margin: 0, backgroundColor: '#090d16', padding: '0.85rem', borderRadius: '8px', border: '1px solid #1e293b', color: '#cbd5e1', fontSize: '0.82rem', fontFamily: 'inherit', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                              {st.detailed_theme}
                            </pre>
                          </div>
                        )}

                        {/* BRAND FOOTER */}
                        <div style={{ marginTop: '0.85rem', textAlign: 'center', fontSize: '0.7rem', color: '#64748b', borderTop: '1px dashed #1e293b', paddingTop: '0.5rem' }}>
                          © Elum Law Associates — All Rights Reserved. Errors & Omissions Excepted (E&OE).
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CLICK & DRAG 2D RESIZE HANDLE (HORIZONTAL & VERTICAL) AT BOTTOM-RIGHT CORNER */}
        {windowState !== 'fullscreen' && (
          <div 
            onMouseDown={handleStartResize}
            title="Click and drag horizontally or vertically to resize dictionary window size"
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '20px',
              height: '20px',
              cursor: 'nwse-resize',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ecc94b',
              color: 'black',
              borderRadius: '4px 0 14px 0',
              userSelect: 'none'
            }}
          >
            <span style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>↘</span>
          </div>
        )}

      </div>
    </div>
  );
};
